import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useCards } from "../../../../hooks/use-cards";
import { useGoalScorers } from "../../../../hooks/use-goal-scorers";
import { useLineups } from "../../../../hooks/use-lineups";
import { useSubstitutions } from "../../../../hooks/use-substitutions";
import { authFetch } from "../../../../lib/auth-fetch";
import {
	selectAwayTeam,
	selectBroadcastId,
	selectFixtureId,
	selectHomeTeam,
} from "../../../../redux/broadcast/broadcast.selectors";

export const useTeamsModule = () => {
	const broadcastId = useSelector(selectBroadcastId);
	const fixtureId = useSelector(selectFixtureId);
	const homeTeam = useSelector(selectHomeTeam);
	const awayTeam = useSelector(selectAwayTeam);

	const lineups = useLineups(fixtureId);
	const substitutions = useSubstitutions(broadcastId);
	const cards = useCards(broadcastId);
	const goalScorers = useGoalScorers(broadcastId);

	const [fetchingLineups, setFetchingLineups] = useState(false);
	const [lineupFetchMsg, setLineupFetchMsg] = useState<string | null>(null);
	const [lastLineupAttempt, setLastLineupAttempt] = useState<Date | null>(null);
	const cooldownRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const lineupRetryRef = useRef<ReturnType<typeof setInterval> | null>(null);

	useEffect(
		() => () => {
			if (lineupRetryRef.current) clearInterval(lineupRetryRef.current);
			if (cooldownRef.current) clearTimeout(cooldownRef.current);
		},
		[],
	);

	const doFetchLineups = async (): Promise<boolean> => {
		setFetchingLineups(true);
		setLineupFetchMsg(null);
		setLastLineupAttempt(new Date());
		try {
			const res = await authFetch(
				`/api/broadcasts/${broadcastId}/fetch-lineups`,
				{ method: "POST" },
			);
			const data = (await res.json()) as { found: boolean };
			if (data.found) {
				setLineupFetchMsg("Lineups fetched!");
				if (lineupRetryRef.current) clearInterval(lineupRetryRef.current);
				setFetchingLineups(false);
				return true;
			}
			setLineupFetchMsg("Not available yet — retrying every 2 min…");
		} catch {
			setLineupFetchMsg("Request failed — retrying every 2 min…");
		}
		cooldownRef.current = setTimeout(() => setFetchingLineups(false), 30_000);
		return false;
	};

	const handleFetchLineups = () => {
		if (lineupRetryRef.current) clearInterval(lineupRetryRef.current);
		doFetchLineups().then((found) => {
			if (!found) {
				lineupRetryRef.current = setInterval(
					() => {
						doFetchLineups().then((ok) => {
							if (ok && lineupRetryRef.current)
								clearInterval(lineupRetryRef.current);
						});
					},
					2 * 60 * 1000,
				);
			}
		});
	};

	return {
		broadcastId,
		fixtureId,
		homeTeam,
		awayTeam,
		lineups,
		substitutions,
		cards,
		goalScorers,
		fetchingLineups,
		lineupFetchMsg,
		lastLineupAttempt,
		lineupRetryRef,
		handleFetchLineups,
	};
};
